import React from 'react';
import { Client, ClientType } from '@1po/1po-bff-fe-spec/generated/my_network/response/MyNetworkClientsResponse';
import { SortOrder } from 'antd/es/table/interface';
import { ColumnType } from 'antd-v5/es/table';
import { TFunction } from 'i18next';
import { ROUTER_GARAGE_DETAILS, ROUTER_MY_NETWORK } from 'app/AppRouter';
import { ArrowDownIcon, ArrowUpIcon, CheckCircleIcon, CircleDash } from 'assets/icons';
import { MyNetworkClientsSort } from 'domains/r1Network/R1Network.types';
import { theme } from 'styles';
import { CenterFlex, Icon, Link, Text } from 'UI';
import { textFormatter } from 'utils';
import { CsvColumn, CsvValue, exportToCsv } from 'utils/csv/csvExporter';
import { isValidDate } from 'utils/format/formattingService';

// eslint-disable-next-line react/display-name
const renderSortIcon = (sort: MyNetworkClientsSort, sortColumn: string) => ({
  sortOrder,
}: {
  sortOrder: string | null;
}) => (
  <Icon
    IconComponent={sortOrder === 'descend' ? ArrowDownIcon : ArrowUpIcon}
    size={16}
    color={sort.field === sortColumn ? theme.color.clear_blue : theme.color.brand_black}
  />
);

const getSortOrder = (sort: MyNetworkClientsSort, field: string): SortOrder | undefined => {
  if (sort.field !== field) return undefined;
  return (sort.direction === 'ASC' ? 'ascend' : 'descend') as SortOrder;
};

const renderDateCell = (dateStr?: string) => {
  if (!dateStr || !isValidDate(dateStr)) {
    return null;
  }
  return <CenterFlex>{textFormatter.formatDateNumeric(new Date(dateStr))}</CenterFlex>;
};

/**
 * Defines table columns for My Network clients
 */
export const getMyNetworkTableColumns = (
  t: TFunction,
  handleClick: (client: Client) => void,
  sort: MyNetworkClientsSort,
): ColumnType<Client>[] => [
  {
    title: t('my_network.clients.company_name', 'Company Name'),
    dataIndex: 'companyName',
    key: 'COMPANY_NAME',
    sortIcon: renderSortIcon(sort, 'COMPANY_NAME'),
    sorter: true,
    sortOrder: getSortOrder(sort, 'COMPANY_NAME'),
    render: (companyName: string, record: Client) => (
      <CenterFlex>
        <Link to={`${ROUTER_MY_NETWORK}${ROUTER_GARAGE_DETAILS}`} additionalAction={() => handleClick(record)}>
          <Text type="sm" displayStyle={'link'} cursor={'pointer'} decoration={'underline'}>
            {companyName ?? ''}
          </Text>
        </Link>
      </CenterFlex>
    ),
  },
  {
    title: t('my_network.clients.tresor_code', 'Tresor code'),
    dataIndex: 'tresorCode',
    key: 'TRESOR_CODE',
    sortIcon: renderSortIcon(sort, 'TRESOR_CODE'),
    sorter: true,
    sortOrder: getSortOrder(sort, 'TRESOR_CODE'),
    render: (tresorCode?: string) => <CenterFlex>{tresorCode ?? ''}</CenterFlex>,
  },
  {
    title: t('my_network.clients.bir_code', 'BIR code'),
    dataIndex: 'birCode',
    key: 'BIR_CODE',
    sortIcon: renderSortIcon(sort, 'BIR_CODE'),
    sorter: true,
    sortOrder: getSortOrder(sort, 'BIR_CODE'),
    render: (birCode?: string) => <CenterFlex>{birCode ?? ''}</CenterFlex>,
  },
  {
    title: t('my_network.clients.dms_code', 'DMS code'),
    dataIndex: 'dmsCode',
    key: 'DMS_CODE',
    render: (dmsCode?: string) => <CenterFlex>{dmsCode ?? ''}</CenterFlex>,
  },
  {
    title: t('my_network.clients.active', 'Active'),
    dataIndex: 'active',
    key: 'ACTIVE',
    render: (active = false) => (
      <CenterFlex>
        {active ? (
          <Icon IconComponent={CheckCircleIcon} size={24} color={theme.color.palette_green_7} noPointer />
        ) : (
          <Icon IconComponent={CircleDash} size={21} noPointer />
        )}
      </CenterFlex>
    ),
  },
  {
    title: t('my_network.clients.client_type', 'Client type'),
    dataIndex: 'clientType',
    key: 'CLIENT_TYPE',
    sortIcon: renderSortIcon(sort, 'CLIENT_TYPE'),
    sorter: true,
    sortOrder: getSortOrder(sort, 'CLIENT_TYPE'),
    render: (clientType?: ClientType) => <CenterFlex>{getClientTypeLabel(clientType ?? '', t)}</CenterFlex>,
  },
  {
    title: t('common.date.start_date', 'Start date'),
    dataIndex: 'startDate',
    key: 'START_DATE',
    sortIcon: renderSortIcon(sort, 'START_DATE'),
    sorter: true,
    sortOrder: getSortOrder(sort, 'START_DATE'),
    render: renderDateCell,
  },
  {
    title: t('common.date.end_date', 'End date'),
    dataIndex: 'endDate',
    key: 'END_DATE',
    sortIcon: renderSortIcon(sort, 'END_DATE'),
    sorter: true,
    sortOrder: getSortOrder(sort, 'END_DATE'),
    render: renderDateCell,
  },
  {
    title: t('my_network.clients.billing_account', 'Billing account'),
    dataIndex: 'billingAccount',
    key: 'BILLING_ACCOUNT',
    render: (billingAccount?: string) => <CenterFlex>{billingAccount ?? ''}</CenterFlex>,
  },
];

/**
 * Defines CSV columns for My Network clients export
 */
export const getMyNetworkCsvColumns = (t: TFunction): CsvColumn<Client>[] => [
  {
    header: t('my_network.clients.company_name', 'Company Name'),
    getValue: (row: Client) => row.companyName ?? '',
  },
  {
    header: t('my_network.clients.tresor_code', 'Tresor code'),
    getValue: (row: Client) => row.tresorCode ?? '',
  },
  {
    header: t('my_network.clients.bir_code', 'BIR code'),
    getValue: (row: Client) => row.birCode ?? '',
  },
  {
    header: t('my_network.clients.dms_code', 'DMS code'),
    getValue: (row: Client) => row.dmsCode ?? '',
  },
  {
    header: t('my_network.clients.active', 'Active'),
    getValue: (row: Client) => row.active ?? false,
    format: (active: CsvValue) => (active ? t('common.yes', 'Yes') : t('common.no', 'No')),
  },
  {
    header: t('my_network.clients.client_type', 'Client type'),
    getValue: (row: Client) => getClientTypeLabel(row.clientType ?? '', t),
  },
  {
    header: t('common.date.start_date', 'Start date'),
    getValue: (row: Client) => row.startDate ?? '',
    format: (date: CsvValue) => {
      if (!date || typeof date !== 'string' || !isValidDate(date)) {
        return '';
      }
      return textFormatter.formatDateNumeric(new Date(date));
    },
  },
  {
    header: t('common.date.end_date', 'End date'),
    getValue: (row: Client) => row.endDate ?? '',
    format: (date: CsvValue) => {
      if (!date || typeof date !== 'string' || !isValidDate(date)) {
        return '';
      }
      return textFormatter.formatDateNumeric(new Date(date));
    },
  },
  {
    header: t('my_network.clients.billing_account', 'Billing account'),
    getValue: (row: Client) => row.billingAccount ?? '',
  },
];

/**
 * Exports My Network clients data to CSV file
 */
export const exportMyNetworkToCSV = (data: Client[], t: TFunction): void => {
  const columns = getMyNetworkCsvColumns(t);

  exportToCsv<Client>(data, columns, {
    filename: 'my_network_clients',
    addDateToFilename: true,
    addBOM: true,
  });
};

export const getClientTypeLabel = (clientType: ClientType | string, t: TFunction) => {
  switch (clientType) {
    case 'R1':
      return t('my_network.clients.client_type.r1', 'R1');
    case 'R2':
      return t('my_network.clients.client_type.r2', 'R2');
    case 'R3':
      return t('my_network.clients.client_type.r3', 'R3');
    case 'R3_MOT':
      return t('my_network.clients.client_type.r3_motrio', 'R3 Motrio');
    default:
      return clientType;
  }
};
